import {
  db,
  forgetAllCursors,
  forgetCursor,
  getAllCursors,
  getDeviceId,
  setCursor,
  type LocalComment,
  type LocalExpense,
  type LocalSettlement,
  type OutboxOperation,
} from './db'
import { tickClock, type VectorClock } from '@/domain/vectorClock'
import type { SplitType } from '@/domain/splitting'
import { newId } from '@/domain/ids'
import { sha256Hex } from '@/domain/sha256'

export interface SyncOperationRequest {
  operationId: string
  entityType: OutboxOperation['entityType']
  entityId: string
  operation: OutboxOperation['operation']
  groupId: string
  payloadJson: string
  vectorClock: VectorClock
  clientTimestamp: string
}

export interface SyncAccepted {
  operationId: string
  entityId: string
  serverSeq: number
  vectorClock: VectorClock
}

export interface SyncRejected {
  operationId: string
  entityId: string
  reason: string
  code: string
}

export interface SyncConflict {
  conflictId: string
  groupId: string
  entityType: string
  entityId: string
  storedPayloadJson: string
  storedVectorClock: VectorClock
  incomingPayloadJson: string
  incomingVectorClock: VectorClock
  conflictingFields: string[]
  detectedAt: string
}

export interface SyncPushResult {
  accepted: SyncAccepted[]
  conflicts: SyncConflict[]
  rejected: SyncRejected[]
  groupCursors: Record<string, number>
}

export interface SyncLogEntry {
  serverSeq: number
  groupId: string
  entityType: string
  entityId: string
  operation: string
  deviceId: string
  payloadJson: string
  vectorClock: VectorClock
  lineageId: string
  sourceGroupId?: string | null
  counterpartGroupId?: string | null
  createdAt: string
}

export interface SyncPullResult {
  entries: SyncLogEntry[]
  groupCursors: Record<string, number>
  snapshots: Array<{
    id: string
    groupId: string
    upToServerSeq: number
    cutoffAt: string
    vectorClock: VectorClock
    stateJson: string
  }>
  hasMore: boolean
}

export interface SyncApi {
  push(request: { deviceId: string; operations: SyncOperationRequest[] }): Promise<SyncPushResult>
  pull(request: {
    deviceId: string
    groupCursors: Record<string, number>
    maxEntries: number
  }): Promise<SyncPullResult>
  acknowledge(groupCursors: Record<string, number>): Promise<void>
  digest?(): Promise<SyncDigest[]>
}

export interface SyncDigest {
  groupId: string
  expenses: number
  settlements: number
  digest: string
}

export interface SyncDrift {
  groupId: string
  device: SyncDigest
  server: SyncDigest
  repaired: boolean
}

export interface EnqueueRequest {
  entityType: OutboxOperation['entityType']
  entityId: string
  operation: OutboxOperation['operation']
  groupId: string
  payload: unknown
}

const MAX_PULL_PAGES = 50

const FULL_RESYNC_VERSION = '1'
const FULL_RESYNC_KEY = 'full-resync-version'

function isRefusal(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status
  if (typeof status !== 'number') return false
  if (status === 401 || status === 408 || status === 429) return false

  return status >= 400 && status < 500
}
const PULL_BATCH_SIZE = 500

export class SyncEngine {
  private flushing: Promise<SyncPushResult> | null = null
  private readonly api: SyncApi
  private readonly isOnline: () => boolean
  private readonly onDrift: (drift: SyncDrift) => void
  private readonly reread = new Set<string>()

  constructor(
    api: SyncApi,
    isOnline: () => boolean = () => navigator.onLine,
    onDrift: (drift: SyncDrift) => void = () => {},
  ) {
    this.api = api
    this.isOnline = isOnline
    this.onDrift = onDrift
  }

  async enqueue(request: EnqueueRequest): Promise<OutboxOperation> {
    const deviceId = await getDeviceId()
    const previous = await this.latestClockFor(request.entityId)

    const operation: OutboxOperation = {
      operationId: newId(),
      entityType: request.entityType,
      entityId: request.entityId,
      operation: request.operation,
      groupId: request.groupId,
      payloadJson: JSON.stringify(request.payload),
      vectorClock: tickClock(previous, deviceId),
      clientTimestamp: new Date().toISOString(),
      sequence: await this.nextSequence(),
      status: 'pending',
      attempts: 0,
      lastError: null,
    }

    await db.outbox.put(operation)
    return operation
  }

  async pendingCount(): Promise<number> {
    return db.outbox.where('status').anyOf('pending', 'inflight').count()
  }

  async rejectedCount(): Promise<number> {
    return db.outbox.where('status').equals('rejected').count()
  }

  async discard(operationId: string): Promise<void> {
    await db.outbox.delete(operationId)
  }

  async retry(operationId: string): Promise<void> {
    await db.outbox.update(operationId, { status: 'pending', lastError: null })
  }

  async flush(): Promise<SyncPushResult> {
    if (this.flushing) return this.flushing

    this.flushing = this.doFlush().finally(() => {
      this.flushing = null
    })

    return this.flushing
  }

  private async doFlush(): Promise<SyncPushResult> {
    const empty: SyncPushResult = { accepted: [], conflicts: [], rejected: [], groupCursors: {} }
    if (!this.isOnline()) return empty

    const pending = await db.outbox.where('status').equals('pending').sortBy('sequence')
    if (pending.length === 0) return empty

    const deviceId = await getDeviceId()

    try {
      const result = await this.api.push({
        deviceId,
        operations: pending.map((operation) => ({
          operationId: operation.operationId,
          entityType: operation.entityType,
          entityId: operation.entityId,
          operation: operation.operation,
          groupId: operation.groupId,
          payloadJson: operation.payloadJson,
          vectorClock: operation.vectorClock,
          clientTimestamp: operation.clientTimestamp,
        })),
      })

      await this.applyPushResult(pending, result)
      return result
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      const refused = isRefusal(error)

      for (const operation of pending) {
        await db.outbox.update(operation.operationId, {
          status: refused ? 'rejected' : 'pending',
          attempts: operation.attempts + 1,
          lastError: message,
        })

        if (refused) await this.release(operation)
      }

      return empty
    }
  }

  private async applyPushResult(
    sent: OutboxOperation[],
    result: SyncPushResult,
  ): Promise<void> {
    const acceptedIds = new Set(result.accepted.map((a) => a.operationId))
    const conflictedEntityIds = new Set(result.conflicts.map((c) => c.entityId))
    const rejectedById = new Map(result.rejected.map((r) => [r.operationId, r]))

    for (const operation of sent) {
      if (acceptedIds.has(operation.operationId)) {
        await db.outbox.delete(operation.operationId)
        await this.markSynced(operation)
        continue
      }

      const rejection = rejectedById.get(operation.operationId)
      if (rejection) {
        await db.outbox.update(operation.operationId, {
          status: 'rejected',
          attempts: operation.attempts + 1,
          lastError: `${rejection.code}: ${rejection.reason}`,
        })
        await this.release(operation)
        continue
      }

      if (conflictedEntityIds.has(operation.entityId)) {
        await db.outbox.delete(operation.operationId)
        continue
      }

      await db.outbox.update(operation.operationId, { status: 'pending' })
    }

    for (const conflict of result.conflicts) {
      await db.conflicts.put({
        conflictId: conflict.conflictId,
        groupId: conflict.groupId,
        entityType: conflict.entityType,
        entityId: conflict.entityId,
        storedPayloadJson: conflict.storedPayloadJson,
        incomingPayloadJson: conflict.incomingPayloadJson,
        conflictingFields: conflict.conflictingFields,
        detectedAt: conflict.detectedAt,
      })
    }
  }

  private async release(operation: OutboxOperation): Promise<void> {
    const stillQueued = await db.outbox
      .where('entityId')
      .equals(operation.entityId)
      .filter((row) => row.status === 'pending' || row.status === 'inflight')
      .count()

    if (stillQueued > 0) return

    if (operation.operation === 'Create') {
      switch (operation.entityType) {
        case 'Expense':
          await db.expenses.delete(operation.entityId)
          break
        case 'Settlement':
          await db.settlements.delete(operation.entityId)
          break
        case 'ExpenseComment':
          await db.comments.delete(operation.entityId)
          break
      }
      return
    }

    const restore = operation.operation === 'Delete'
      ? { pending: false, isDeleted: false }
      : { pending: false }

    switch (operation.entityType) {
      case 'Expense':
        await db.expenses.update(operation.entityId, restore)
        break
      case 'Settlement':
        await db.settlements.update(operation.entityId, restore)
        break
      case 'ExpenseComment':
        await db.comments.update(operation.entityId, restore)
        break
    }
  }

  private async markSynced(operation: OutboxOperation): Promise<void> {
    const stillQueued = await db.outbox
      .where('entityId')
      .equals(operation.entityId)
      .filter((row) => row.status !== 'rejected')
      .count()

    if (stillQueued > 0) return

    switch (operation.entityType) {
      case 'Expense':
        await db.expenses.update(operation.entityId, { pending: false })
        break
      case 'Settlement':
        await db.settlements.update(operation.entityId, { pending: false })
        break
      case 'ExpenseComment':
        await db.comments.update(operation.entityId, { pending: false })
        break
    }
  }

  async pull(): Promise<void> {
    if (!this.isOnline()) return

    await this.rereadEverythingOnce()
    await this.pullPages()
    await this.checkAgainstServer()
  }

  private async rereadEverythingOnce(): Promise<void> {
    const done = await db.meta.get(FULL_RESYNC_KEY)
    if (done?.value === FULL_RESYNC_VERSION) return

    await forgetAllCursors()
    await db.meta.put({ key: FULL_RESYNC_KEY, value: FULL_RESYNC_VERSION })
  }

  private async checkAgainstServer(): Promise<void> {
    if (!this.api.digest) return

    const drifted = await this.driftedGroups()
    const toReread = drifted.filter((server) => !this.reread.has(server.groupId))
    if (toReread.length === 0) return

    for (const server of toReread) {
      this.reread.add(server.groupId)
      await this.dropServerCopy(server.groupId)
    }
    await this.pullPages()

    const after = new Map((await this.serverDigests()).map((server) => [server.groupId, server]))
    for (const before of toReread) {
      const server = after.get(before.groupId) ?? before
      const device = await localDigest(before.groupId)
      this.onDrift({ groupId: before.groupId, device, server, repaired: device.digest === server.digest })
    }
  }

  private async driftedGroups(): Promise<SyncDigest[]> {
    const drifted: SyncDigest[] = []

    for (const server of await this.serverDigests()) {
      if (await this.hasLocalWork(server.groupId)) continue
      if ((await localDigest(server.groupId)).digest !== server.digest) drifted.push(server)
    }

    return drifted
  }

  private async serverDigests(): Promise<SyncDigest[]> {
    try {
      return (await this.api.digest?.()) ?? []
    } catch {
      return []
    }
  }

  private async hasLocalWork(groupId: string): Promise<boolean> {
    const queued = await db.outbox
      .where('groupId')
      .equals(groupId)
      .filter((row) => row.status === 'pending' || row.status === 'inflight')
      .count()
    if (queued > 0) return true

    return (await db.conflicts.where('groupId').equals(groupId).count()) > 0
  }

  private async dropServerCopy(groupId: string): Promise<void> {
    await db.expenses.where('groupId').equals(groupId).delete()
    await db.settlements.where('groupId').equals(groupId).delete()
    await db.comments.where('groupId').equals(groupId).delete()
    await forgetCursor(groupId)
  }

  private async pullPages(): Promise<void> {
    const deviceId = await getDeviceId()

    for (let page = 0; page < MAX_PULL_PAGES; page++) {
      const groupCursors = await getAllCursors()

      const result = await this.api.pull({
        deviceId,
        groupCursors,
        maxEntries: PULL_BATCH_SIZE,
      })

      for (const entry of result.entries) await this.applyEntry(entry)

      for (const [groupId, serverSeq] of Object.entries(result.groupCursors)) {
        await setCursor(groupId, serverSeq)
      }

      if (Object.keys(result.groupCursors).length > 0) {
        await this.api.acknowledge(result.groupCursors)
      }

      if (!result.hasMore) break
    }
  }

  private async applyEntry(entry: SyncLogEntry): Promise<void> {
    if (await this.hasUnsentLocalChange(entry.entityId)) return

    if (entry.operation === 'Delete') {
      await this.applyDelete(entry)
      return
    }

    let payload: Record<string, unknown>
    try {
      payload = JSON.parse(entry.payloadJson) as Record<string, unknown>
    } catch {
      return
    }

    if (isTransferMarker(entry, payload)) {
      await this.applyTransferMarker(entry, String(payload.movedTo))
      return
    }

    switch (entry.entityType) {
      case 'Expense':
        await db.expenses.put(toLocalExpense(payload, entry))
        break
      case 'Settlement':
        await db.settlements.put(toLocalSettlement(payload, entry))
        break
      case 'ExpenseComment':
        await db.comments.put(toLocalComment(payload, entry))
        break
    }
  }

  private async applyTransferMarker(entry: SyncLogEntry, movedTo: string): Promise<void> {
    const local = await db.expenses.get(entry.entityId)
    if (!local || local.groupId === movedTo) return

    await db.expenses.delete(entry.entityId)

    await db.comments.where('expenseId').equals(entry.entityId).delete()
  }

  private async applyDelete(entry: SyncLogEntry): Promise<void> {
    const patch = { isDeleted: true, pending: false, vectorClock: entry.vectorClock }

    switch (entry.entityType) {
      case 'Expense':
        await db.expenses.update(entry.entityId, patch)
        break
      case 'Settlement':
        await db.settlements.update(entry.entityId, patch)
        break
      case 'ExpenseComment':
        await db.comments.update(entry.entityId, { isDeleted: true, pending: false })
        break
    }
  }

  private async hasUnsentLocalChange(entityId: string): Promise<boolean> {
    const queued = await db.outbox
      .where('entityId')
      .equals(entityId)
      .filter((row) => row.status === 'pending' || row.status === 'inflight')
      .count()
    if (queued > 0) return true

    const expense = await db.expenses.get(entityId)
    if (expense?.pending) return true

    const settlement = await db.settlements.get(entityId)
    if (settlement?.pending) return true

    const comment = await db.comments.get(entityId)
    return Boolean(comment?.pending)
  }

  private async latestClockFor(entityId: string): Promise<VectorClock> {
    const queued = await db.outbox
      .where('entityId')
      .equals(entityId)
      .filter((row) => row.status !== 'rejected')
      .sortBy('sequence')

    if (queued.length > 0) return queued[queued.length - 1].vectorClock

    const expense = await db.expenses.get(entityId)
    if (expense) return expense.vectorClock

    const settlement = await db.settlements.get(entityId)
    if (settlement) return settlement.vectorClock

    const comment = await db.comments.get(entityId)
    if (comment) return comment.vectorClock

    return {}
  }

  private async nextSequence(): Promise<number> {
    const last = await db.outbox.orderBy('sequence').last()
    return (last?.sequence ?? 0) + 1
  }
}

const SPLIT_TYPES: SplitType[] = ['Equal', 'Percentage', 'Shares', 'ExactAmount', 'Itemized']

function readSplitType(value: unknown): SplitType {
  if (typeof value === 'string' && SPLIT_TYPES.includes(value as SplitType)) {
    return value as SplitType
  }
  if (typeof value === 'number' && SPLIT_TYPES[value]) return SPLIT_TYPES[value]
  return 'Equal'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WirePayload = Record<string, any>
function isTransferMarker(entry: SyncLogEntry, payload: WirePayload): boolean {
  return (
    entry.operation === 'Transfer' &&
    entry.entityType === 'Expense' &&
    typeof payload.movedTo === 'string'
  )
}

function toLocalExpense(payload: WirePayload, entry: SyncLogEntry): LocalExpense {
  return {
    id: String(payload.id ?? entry.entityId),
    groupId: String(payload.groupId ?? entry.groupId),
    paidByMemberId: String(payload.paidByMemberId ?? ''),
    description: String(payload.description ?? ''),
    amount: Number(payload.amount ?? 0),
    currency: String(payload.currency ?? 'CAD'),
    amountInBaseCurrency: Number(payload.amountInBaseCurrency ?? payload.amount ?? 0),
    exchangeRate: Number(payload.exchangeRate ?? 1),
    spentAt: String(payload.spentAt ?? entry.createdAt),
    splitType: readSplitType(payload.splitType),
    receiptId: payload.receiptId ?? null,
    notes: payload.notes ?? null,
    categoryKey: payload.categoryKey ?? null,
    payers:
      (payload.payers ?? []).length > 0
        ? payload.payers.map((payer: WirePayload) => ({
            memberId: String(payer.memberId),
            amount: Number(payer.amount ?? 0),
            amountInBaseCurrency: Number(payer.amountInBaseCurrency ?? payer.amount ?? 0),
          }))
        : [
            {
              memberId: String(payload.paidByMemberId ?? ''),
              amount: Number(payload.amount ?? 0),
              amountInBaseCurrency: Number(payload.amountInBaseCurrency ?? payload.amount ?? 0),
            },
          ],
    splits: (payload.splits ?? []).map((split: WirePayload) => ({
      memberId: String(split.memberId),
      amount: Number(split.amount ?? 0),
      amountInBaseCurrency: Number(split.amountInBaseCurrency ?? split.amount ?? 0),
      inputValue: split.inputValue ?? null,
    })),
    items: (payload.items ?? []).map((item: WirePayload) => ({
      id: item.id ?? null,
      description: String(item.description ?? ''),
      amount: Number(item.amount ?? 0),
      quantity: Number(item.quantity ?? 1),
      sortOrder: Number(item.sortOrder ?? 0),
      memberIds: item.members ?? item.memberIds ?? [],
    })),
    revision: Number(payload.revision ?? 1),
    isDeleted: Boolean(payload.isDeleted ?? false),
    vectorClock: entry.vectorClock,
    serverSeq: entry.serverSeq,
    pending: false,
  }
}

function toLocalSettlement(payload: WirePayload, entry: SyncLogEntry): LocalSettlement {
  return {
    id: String(payload.id ?? entry.entityId),
    groupId: String(payload.groupId ?? entry.groupId),
    fromMemberId: String(payload.fromMemberId ?? ''),
    toMemberId: String(payload.toMemberId ?? ''),
    amount: Number(payload.amount ?? 0),
    currency: String(payload.currency ?? 'CAD'),
    amountInBaseCurrency: Number(payload.amountInBaseCurrency ?? payload.amount ?? 0),
    settledAt: String(payload.settledAt ?? entry.createdAt),
    note: payload.note ?? null,
    isDeleted: Boolean(payload.isDeleted ?? false),
    vectorClock: entry.vectorClock,
    serverSeq: entry.serverSeq,
    pending: false,
  }
}

function toLocalComment(payload: WirePayload, entry: SyncLogEntry): LocalComment {
  return {
    id: String(payload.id ?? entry.entityId),
    expenseId: String(payload.expenseId ?? ''),
    groupId: String(payload.groupId ?? entry.groupId),
    authorMemberId: String(payload.authorMemberId ?? ''),
    parentCommentId: payload.parentCommentId ?? null,
    body: String(payload.body ?? ''),
    createdAt: String(payload.createdAt ?? entry.createdAt),
    isDeleted: Boolean(payload.isDeleted ?? false),
    vectorClock: entry.vectorClock,
    pending: false,
  }
}

function cents(amount: number): string {
  return String(Math.round(amount * 100))
}

export async function localDigest(groupId: string): Promise<SyncDigest> {
  const expenses = (await db.expenses.where('groupId').equals(groupId).toArray()).filter((row) => !row.isDeleted)
  const settlements = (await db.settlements.where('groupId').equals(groupId).toArray()).filter(
    (row) => !row.isDeleted,
  )

  const lines = [
    ...expenses.map((row) => `e:${row.id}:${row.revision}:${cents(row.amountInBaseCurrency)}`),
    ...settlements.map((row) => `s:${row.id}:${cents(row.amountInBaseCurrency)}`),
  ].sort()

  return {
    groupId,
    expenses: expenses.length,
    settlements: settlements.length,
    digest: sha256Hex(lines.join('\n')),
  }
}
